import pick from 'lodash/pick';
import axios from 'axios';
import config from '../../config';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import {
  TRANSITION_REQUEST_PAYMENT,
  TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY,
  TRANSITION_CONFIRM_PAYMENT,
} from '../../util/transaction';
import * as log from '../../util/log';
import { fetchCurrentUserHasOrdersSuccess, fetchCurrentUser } from '../../ducks/user.duck';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { adPurchased } from '../../custom-ducks/utility.duck';
import { getBearerToken } from '../../util/cookie';
import { initiatePrivileged, transitionPrivileged } from '../../util/api';

// ================ Action types ================ //

export const SET_INITAL_VALUES = 'app/CheckoutPage/SET_INITIAL_VALUES';

export const INITIATE_ORDER_REQUEST = 'app/CheckoutPage/INITIATE_ORDER_REQUEST';
export const INITIATE_ORDER_SUCCESS = 'app/CheckoutPage/INITIATE_ORDER_SUCCESS';
export const INITIATE_ORDER_ERROR = 'app/CheckoutPage/INITIATE_ORDER_ERROR';

export const CONFIRM_PAYMENT_REQUEST = 'app/CheckoutPage/CONFIRM_PAYMENT_REQUEST';
export const CONFIRM_PAYMENT_SUCCESS = 'app/CheckoutPage/CONFIRM_PAYMENT_SUCCESS';
export const CONFIRM_PAYMENT_ERROR = 'app/CheckoutPage/CONFIRM_PAYMENT_ERROR';

export const SPECULATE_TRANSACTION_REQUEST = 'app/ListingPage/SPECULATE_TRANSACTION_REQUEST';
export const SPECULATE_TRANSACTION_SUCCESS = 'app/ListingPage/SPECULATE_TRANSACTION_SUCCESS';
export const SPECULATE_TRANSACTION_ERROR = 'app/ListingPage/SPECULATE_TRANSACTION_ERROR';

export const STRIPE_CUSTOMER_REQUEST = 'app/CheckoutPage/STRIPE_CUSTOMER_REQUEST';
export const STRIPE_CUSTOMER_SUCCESS = 'app/CheckoutPage/STRIPE_CUSTOMER_SUCCESS';
export const STRIPE_CUSTOMER_ERROR = 'app/CheckoutPage/STRIPE_CUSTOMER_ERROR';

// ================ Reducer ================ //

const initialState = {
  listing: null,
  bookingData: null,
  bookingDates: null,
  speculateTransactionInProgress: false,
  speculateTransactionError: null,
  speculatedTransaction: null,
  transaction: null,
  initiateOrderError: null,
  confirmPaymentError: null,
  stripeCustomerFetched: false,
};

export default function checkoutPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITAL_VALUES:
      return { ...initialState, ...payload };

    case SPECULATE_TRANSACTION_REQUEST:
      return {
        ...state,
        speculateTransactionInProgress: true,
        speculateTransactionError: null,
        speculatedTransaction: null,
      };
    case SPECULATE_TRANSACTION_SUCCESS:
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculatedTransaction: payload.transaction,
      };
    case SPECULATE_TRANSACTION_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculateTransactionError: payload,
      };

    case INITIATE_ORDER_REQUEST:
      return { ...state, initiateOrderError: null };
    case INITIATE_ORDER_SUCCESS:
      return { ...state, transaction: payload };
    case INITIATE_ORDER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, initiateOrderError: payload };

    case CONFIRM_PAYMENT_REQUEST:
      return { ...state, confirmPaymentError: null };
    case CONFIRM_PAYMENT_SUCCESS:
      return state;
    case CONFIRM_PAYMENT_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, confirmPaymentError: payload };

    case STRIPE_CUSTOMER_REQUEST:
      return { ...state, stripeCustomerFetched: false };
    case STRIPE_CUSTOMER_SUCCESS:
      return { ...state, stripeCustomerFetched: true };
    case STRIPE_CUSTOMER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, stripeCustomerFetchError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const initiateOrderRequest = () => ({ type: INITIATE_ORDER_REQUEST });

const initiateOrderSuccess = order => ({
  type: INITIATE_ORDER_SUCCESS,
  payload: order,
});

const initiateOrderError = e => ({
  type: INITIATE_ORDER_ERROR,
  error: true,
  payload: e,
});

const confirmPaymentRequest = () => ({ type: CONFIRM_PAYMENT_REQUEST });

const confirmPaymentSuccess = orderId => ({
  type: CONFIRM_PAYMENT_SUCCESS,
  payload: orderId,
});

const confirmPaymentError = e => ({
  type: CONFIRM_PAYMENT_ERROR,
  error: true,
  payload: e,
});

export const speculateTransactionRequest = () => ({ type: SPECULATE_TRANSACTION_REQUEST });

export const speculateTransactionSuccess = transaction => ({
  type: SPECULATE_TRANSACTION_SUCCESS,
  payload: { transaction },
});

export const speculateTransactionError = e => ({
  type: SPECULATE_TRANSACTION_ERROR,
  error: true,
  payload: e,
});

export const stripeCustomerRequest = () => ({ type: STRIPE_CUSTOMER_REQUEST });
export const stripeCustomerSuccess = () => ({ type: STRIPE_CUSTOMER_SUCCESS });
export const stripeCustomerError = e => ({
  type: STRIPE_CUSTOMER_ERROR,
  error: true,
  payload: e,
});

/* ================ Thunks ================ */

export const initiateOrder = (orderParams, transactionId, type, couponInfo) => (dispatch, getState, sdk) => {
  dispatch(initiateOrderRequest());
  const toDate = v => (v instanceof Date ? v : new Date(v));

const roundUpToNext5Min = d => {
  const ms = +d;
  const five = 5 * 60 * 1000;
  return new Date(Math.ceil(ms / five) * five);
};

const ensureFutureBooking = (startRaw, endRaw) => {
  const now = new Date();
  let start = startRaw ? toDate(startRaw) : null;
  let end = endRaw ? toDate(endRaw) : null;

  // If either date is invalid, let Flex error be clearer, but don’t send nonsense:
  if (start && isNaN(+start)) return { start: null, end: null };
  if (end && isNaN(+end)) return { start: null, end: null };

  // If start missing: default to next 5 minutes
  if (!start) start = roundUpToNext5Min(now);

  // If start is in the past: move it to next 5 minutes
  if (+start < +now) start = roundUpToNext5Min(now);

  // Default duration 60 minutes if end missing or not after start
  if (!end || +end <= +start) end = new Date(+start + 60 * 60 * 1000);

  return { start, end };
};
  const { start, end } = ensureFutureBooking(orderParams.bookingStart, orderParams.bookingEnd);

const normalizedOrderParams = {
  ...orderParams,
  bookingStart: start,
  bookingEnd: end,
};
  const bodyParamsBase = {
  processAlias: type,
  params: {
    ...normalizedOrderParams,
    cardToken: 'CheckoutPage_speculative_card_token',
  },
};
  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  // transition/request-payment and transition/request-payment-after-enquiry
  // are marked :privileged? true in process.edn (custom pricing for
  // coupons), so Flex only accepts them from our own trusted server —
  // see server/api/checkout/{initiate,transition}Privileged.js.
  const bodyParams = transactionId
    ? { ...bodyParamsBase, id: transactionId, transition: TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY }
    : { ...bodyParamsBase, transition: TRANSITION_REQUEST_PAYMENT };

  const orderData = {
    coachId: couponInfo && couponInfo.coachId,
    couponCode: couponInfo && couponInfo.code,
  };

  const apiCall = transactionId ? transitionPrivileged : initiatePrivileged;

  return apiCall({ isSpeculative: false, bodyParams, orderData, queryParams })
    .then(result => {
      const entities = denormalisedResponseEntities({ data: result.data });
      const order = entities[0];
      dispatch(initiateOrderSuccess(order));
      dispatch(fetchCurrentUserHasOrdersSuccess(true));
      dispatch(adPurchased(order.id.uuid))
      return order;
    })
    .catch(e => {
      dispatch(initiateOrderError(storableError(e)));
      const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
      log.error(e, 'initiate-order-failed', {
        ...transactionIdMaybe,
        listingId: orderParams.listingId.uuid,
       bookingStart: normalizedOrderParams.bookingStart,
       bookingEnd: normalizedOrderParams.bookingEnd,
      });
      throw e;
    });
};

export const confirmPayment = orderParams => (dispatch, getState, sdk) => {
  dispatch(confirmPaymentRequest());

  const bodyParams = {
    id: orderParams.transactionId,
    transition: TRANSITION_CONFIRM_PAYMENT,
    params: {},
  };

  return sdk.transactions
    .transition(bodyParams)
    .then(response => {
      const order = response.data.data;
      dispatch(confirmPaymentSuccess(order.id));
      return order;
    })
    .catch(e => {
      dispatch(confirmPaymentError(storableError(e)));
      const transactionIdMaybe = orderParams.transactionId
        ? { transactionId: orderParams.transactionId.uuid }
        : {};
      log.error(e, 'initiate-order-failed', {
        ...transactionIdMaybe,
      });
      throw e;
    });
};

export const sendMessage = params => (dispatch, getState, sdk) => {
  const message = params.message;
  const orderId = params.id;

  if (message) {
    return sdk.messages
      .send({ transactionId: orderId, content: message })
      .then(() => {
        return { orderId, messageSuccess: true };
      })
      .catch(e => {
        log.error(e, 'initial-message-send-failed', { txId: orderId });
        return { orderId, messageSuccess: false };
      });
  } else {
    return Promise.resolve({ orderId, messageSuccess: true });
  }
};

/**
 * Initiate the speculative transaction with the given booking details
 *
 * The API allows us to do speculative transaction initiation and
 * transitions. This way we can create a test transaction and get the
 * actual pricing information as if the transaction had been started,
 * without affecting the actual data.
 *
 * We store this speculative transaction in the page store and use the
 * pricing info for the booking breakdown to get a proper estimate for
 * the price with the chosen information.
 */
export const speculateTransaction = (params, type, couponInfo) => (dispatch, getState, sdk) => {
  dispatch(speculateTransactionRequest());

  const toDate = v => (v instanceof Date ? v : new Date(v));
  const roundUpToNext5Min = d => {
    const five = 5 * 60 * 1000;
    return new Date(Math.ceil(+d / five) * five);
  };
  const ensureFutureBooking = (startRaw, endRaw) => {
    const now = new Date();
    let start = startRaw != null ? toDate(startRaw) : null;
    let end = endRaw != null ? toDate(endRaw) : null;

    if (start && isNaN(+start)) start = null;
    if (end && isNaN(+end)) end = null;

    if (!start || +start < +now) start = roundUpToNext5Min(now);
    if (!end || +end <= +start) end = new Date(+start + 60 * 60 * 1000);

    return { start, end };
  };

  const { start, end } = ensureFutureBooking(params.bookingStart, params.bookingEnd);

  const normalizedParams = {
    ...params,
    bookingStart: start,
    bookingEnd: end,
  };

  const bodyParams = {
    transition: TRANSITION_REQUEST_PAYMENT,
    processAlias: type,
    params: {
      ...normalizedParams,
      cardToken: 'CheckoutPage_speculative_card_token',
    },
  };

  const queryParams = { include: ['booking', 'provider'], expand: true };

  const orderData = {
    coachId: couponInfo && couponInfo.coachId,
    couponCode: couponInfo && couponInfo.code,
  };

  // See initiateOrder above — transition/request-payment is privileged now,
  // so even the price preview has to go through our own trusted server.
  // Resolves (never rejects) with { ok } so callers — specifically
  // CheckoutPage's coupon flow — can tell a coupon-caused failure apart
  // from success and fall back to the undiscounted price instead of
  // leaving the whole page stuck behind speculateTransactionError.
  return initiatePrivileged({ isSpeculative: true, bodyParams, orderData, queryParams })
    .then(result => {
      const entities = denormalisedResponseEntities({ data: result.data });
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the initiate-privileged speculative response');
      }
      dispatch(speculateTransactionSuccess(entities[0]));
      return { ok: true };
    })
    .catch(e => {
      const { listingId, bookingStart, bookingEnd } = normalizedParams;
      log.error(e, 'speculate-transaction-failed', {
        listingId: listingId?.uuid || listingId,
        bookingStart,
        bookingEnd,
      });
      dispatch(speculateTransactionError(storableError(e)));
      return { ok: false, error: e };
    });
};

/**
 * Preview-only coupon check for the checkout "Apply" button. Calls
 * coach-saas-service directly from the browser — purely informational.
 * The charge itself is only ever computed by initiatePrivileged/
 * transitionPrivileged on our own server, which re-validates the code
 * independently (see server/api-util/couponClient.js) rather than trusting
 * this result.
 */
export const validateCoupon = ({ coachId, code, listingId }) => async (dispatch, getState, sdk) => {
  try {
    const res = await axios.post(
      `${config.coachSaaSService}coupons/validate`,
      { coachId, code, listingId },
      { headers: { 'content-type': 'application/json', Authorization: await getBearerToken(sdk) } }
    );
    return res.data;
  } catch (e) {
    return (e.response && e.response.data) || { ok: false, error: 'validation-failed' };
  }
};

// StripeCustomer is a relantionship to currentUser
// We need to fetch currentUser with correct params to include relationship
export const stripeCustomer = () => (dispatch, getState, sdk) => {
  dispatch(stripeCustomerRequest());

  return dispatch(fetchCurrentUser({ include: ['stripeCustomer.defaultPaymentMethod'] }))
    .then(response => {
      dispatch(stripeCustomerSuccess());
    })
    .catch(e => {
      dispatch(stripeCustomerError(storableError(e)));
    });
};
